import { RefObject, useEffect, useState } from 'react';

export enum HeaderScrollState {
  TOP = 'top',
  SCROLLED = 'scrolled',
  CTA = 'cta'
}

// Django's fixed-menu.ts switches to the CTA this far before the anchor's bottom reaches the header
const CTA_OFFSET = 35;

/**
 * Where the page is relative to the fixed header and an anchor (the bsdir hero's form card):
 * TOP until the page scrolls past the header, SCROLLED until the anchor's bottom passes under
 * the header, then CTA. Always TOP without an anchor, so headers that don't opt in never change.
 */
export const useHeaderScrollState = (
  headerRef: RefObject<HTMLElement>,
  anchorRef?: RefObject<HTMLElement>
): HeaderScrollState => {
  const [state, setState] = useState(HeaderScrollState.TOP);

  useEffect(() => {
    if (!anchorRef) {
      return undefined;
    }

    const update = (): void => {
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      const anchor = anchorRef.current;
      const ctaPoint = anchor
        ? anchor.getBoundingClientRect().top + window.scrollY + anchor.offsetHeight - headerHeight - CTA_OFFSET
        : Infinity;

      if (window.scrollY < headerHeight) {
        setState(HeaderScrollState.TOP);
      } else {
        setState(window.scrollY < ctaPoint ? HeaderScrollState.SCROLLED : HeaderScrollState.CTA);
      }
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [headerRef, anchorRef]);

  return state;
};

export default useHeaderScrollState;
